import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { Button, Checkbox, Label, TextInput } from '@c2fo/liquidity';
import FilterDrawer from '../../FilterDrawer';
import DateRangePicker from '../shared/DateRangePicker';
import { validateNumber } from '../utils';

export interface IneligibleInvoiceFilterOptions {
  dueDate?: {
    from?: string | null;
    to?: string | null;
  };
  originalAmount?: {
    from?: number | null;
    to?: number | null;
  };
  lockedReason?: {
    isAdjusted?: boolean;
    isPastDue?: boolean;
    isReservedByCustomer?: boolean;
  };
}

const IneligibleInvoiceFilters = ({
  filterOpen,
  setFilterOpen,
  filters,
  handleFilters,
  currency,
}: {
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  filters?: IneligibleInvoiceFilterOptions;
  handleFilters: (filters: IneligibleInvoiceFilterOptions) => void;
  currency: string;
}) => {
  const { t } = useTranslation();
  const { dueDate, originalAmount, lockedReason } = filters ?? {};

  const [isFromDatePickerOpen, setIsFromDatePickerOpen] = useState(false);
  const [isToDatePickerOpen, setIsToDatePickerOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState<'allDates' | 'dueDate'>(dueDate ? 'dueDate' : 'allDates');

  // Local state for debounced input values for amount filters
  const [amountFromDebounce, setAmountFromDebounce] = useState<string>(originalAmount?.from?.toString() || '');
  const [amountToDebounce, setAmountToDebounce] = useState<string>(originalAmount?.to?.toString() || '');

  // If due date radio was selected but no From or To date selected, reset to All Dates radio on close
  useEffect(() => {
    if (!filterOpen && !dueDate) {
      setSelectedDate('allDates');
    }
  }, [filterOpen, dueDate]);

  useEffect(() => {
    // Sync local amount values when urlState filters change outside of filter drawer (e.g. Clear filters)
    if (!filterOpen) {
      setAmountFromDebounce(originalAmount?.from?.toString() || '');
      setAmountToDebounce(originalAmount?.to?.toString() || '');
    }
  }, [originalAmount?.from, originalAmount?.to, filterOpen]);

  useDebounce(
    () => {
      // Debounce call to update urlState when filter is open and user is typing
      // Checking for open filter prevents circular updates if user clicks Clear filters which updates local amount values from urlState
      if (filterOpen) {
        handleFilters({ originalAmount: { from: amountFromDebounce === '' ? null : Number(amountFromDebounce) } });
      }
    },
    500,
    [amountFromDebounce]
  );

  useDebounce(
    () => {
      if (filterOpen) {
        handleFilters({ originalAmount: { to: amountToDebounce === '' ? null : Number(amountToDebounce) } });
      }
    },
    500,
    [amountToDebounce]
  );

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        {/* Invoice Deposit Date Filter */}
        <div className="w-full space-y-1">
          <Label>{t('core.dates')}</Label>
          <div className="rounded border border-stroke bg-canvas p-4">
            <ul className="space-y-2">
              {/* All Deposit Dates i.e. no date filter applied */}
              <li>
                <label htmlFor="allDates" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="allDates"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'allDates'}
                    onChange={() => {
                      setSelectedDate('allDates');
                      handleFilters({
                        dueDate: { from: null, to: null },
                      });
                    }}
                    type="radio"
                  />
                  {t('invoiceCentral.allDepositDates')}
                </label>
              </li>
              {/* Original Due Date */}
              <li>
                <label htmlFor="originalDueDate" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="originalDueDate"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'dueDate'}
                    onChange={() => {
                      setSelectedDate('dueDate');
                    }}
                    type="radio"
                  />
                  {t('core.originalDueDate')}
                </label>
                {selectedDate === 'dueDate' && (
                  <DateRangePicker
                    to={dueDate?.to ?? null}
                    from={dueDate?.from ?? null}
                    onFromChange={(date) => handleFilters({ dueDate: { from: date } })}
                    onToChange={(date) => handleFilters({ dueDate: { to: date } })}
                    toOpen={isToDatePickerOpen}
                    fromOpen={isFromDatePickerOpen}
                    onToOpenChange={setIsToDatePickerOpen}
                    onFromOpenChange={setIsFromDatePickerOpen}
                    name="Original Due Date"
                  />
                )}
              </li>
            </ul>
          </div>
        </div>
        {/* Invoice Amount Filter */}
        <div className="w-full space-y-1">
          <Label>{t(`core.invoiceAmount`)}</Label>
          <div className="space-y-2 rounded border border-stroke bg-canvas p-4">
            <div>
              <Label htmlFor="from" className="capitalize">
                {t(`core.from`)}
              </Label>
              <div className="flex items-center gap-2">
                <TextInput
                  id="from"
                  name="from"
                  onChange={(e) => {
                    if (validateNumber(e.target.value)) {
                      setAmountFromDebounce(e.target.value);
                    }
                  }}
                  size="sm"
                  type="text"
                  value={amountFromDebounce}
                />
                {currency}
              </div>
            </div>
            <div>
              <Label htmlFor="to" className="capitalize">
                {t(`core.to`)}
              </Label>
              <div className="flex items-center gap-2">
                <TextInput
                  id="to"
                  name="to"
                  onChange={(e) => {
                    if (validateNumber(e.target.value)) {
                      setAmountToDebounce(e.target.value);
                    }
                  }}
                  size="sm"
                  type="text"
                  value={amountToDebounce}
                />
                {currency}
              </div>
            </div>
          </div>
        </div>
        {/* Ineligibility Reason Filter */}
        <div className="w-full space-y-1">
          <Label>{t(`invoiceCentral.ineligibilityReason`)}</Label>
          <div className="space-y-2 rounded border border-stroke bg-canvas p-4">
            <Checkbox
              checked={lockedReason?.isAdjusted}
              color="secondary"
              id="isAdjusted"
              label={t('taker.invoiceStatus.isAdjusted')}
              onChange={(e) => handleFilters({ lockedReason: { isAdjusted: e.target.checked } })}
              value="isAdjusted"
            />
            <Checkbox
              checked={lockedReason?.isPastDue}
              color="secondary"
              id="isPastDue"
              label={t('taker.invoiceStatus.isPastDue')}
              onChange={(e) => handleFilters({ lockedReason: { isPastDue: e.target.checked } })}
              value="isPastDue"
            />
            <Checkbox
              checked={lockedReason?.isReservedByCustomer}
              color="secondary"
              id="isReservedByCustomer"
              label={t('taker.invoiceStatus.isMakerExcluded')}
              onChange={(e) => handleFilters({ lockedReason: { isReservedByCustomer: e.target.checked } })}
              value="isReservedByCustomer"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default IneligibleInvoiceFilters;
